import React, { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApiAuth } from '../utils/api-auth'
import { useAcctDetailsApi } from '../api/account-details-api'

import { AccountDetails } from '../types'
import { FormattedFormProps } from '../components/Checking/CoverDraftPhaseThree/UpdateCoverDraftForm'
import { useUpdateCoverDraftApi } from '../api/update-coverdraft-api'
import {
  useAccountDetailsTagging,
  accountDetailsServerErrors,
} from '../utils/tagging/accountDetailsTagging'

export interface AccountDetailsProviderProps {
  accountDetails: AccountDetails | undefined
  isAccountDetailsLoading: boolean
  hasAccountDetailsError: boolean
  updateAccountNickname: (nickname: string) => Promise<void>
  updateCoverDraft: (values: FormattedFormProps) => Promise<void>
  updateRaiseYourRate: () => Promise<void>
}

export const AccountDetailsContext = createContext<
  AccountDetailsProviderProps | undefined
>(undefined)

export const AccountDetailsProvider: React.FC<{
  // optional param to assist in mocking state in unit tests. Not used in actual code
  mockState?: Partial<AccountDetailsProviderProps>
}> = ({ children, mockState }) => {
  const { id } = useParams<{ id: string }>()
  const { guid } = useApiAuth()
  const { fetchAccountDetails, updateAccountDetails } = useAcctDetailsApi()
  const { updateCoverDraft } = useUpdateCoverDraftApi()
  const { accountDetailsPageViewEvent } = useAccountDetailsTagging()
  const [accountDetails, setAccountDetails] = useState<
    AccountDetails | undefined
  >(mockState?.accountDetails)
  const [isAccountDetailsLoading, setIsAccountDetailsLoading] = useState(
    mockState?.isAccountDetailsLoading || false,
  )
  const [hasAccountDetailsError, setHasAccountDetailsError] = useState(
    mockState?.hasAccountDetailsError || false,
  )

  const getAccountDetails = async (): Promise<void> => {
    setAccountDetails(undefined)
    setIsAccountDetailsLoading(true)
    setHasAccountDetailsError(false)
    try {
      const details = await fetchAccountDetails()
      setAccountDetails(details)
      accountDetailsPageViewEvent(details)
    } catch (err) {
      setHasAccountDetailsError(true)
      accountDetailsServerErrors.getDetailsFailure(err as Error)
    } finally {
      setIsAccountDetailsLoading(false)
    }
  }

  const updateAccountNickname = async (nickname: string): Promise<void> => {
    const response = await updateAccountDetails({ accountNickname: nickname })
    setAccountDetails(response)
  }

  const updateRaiseYourRate = async (): Promise<void> => {
    const response = await updateAccountDetails({ raiseYourRate: true })
    setAccountDetails(response)
  }

  const coverDraftUpdate = async (
    values: FormattedFormProps,
  ): Promise<void> => {
    await updateCoverDraft(values)
    /* istanbul ignore else */
    if (accountDetails) {
      setAccountDetails({
        ...accountDetails,
        allowanceCurrentAmount: values.coverDraftAmount,
        allowanceCustomerIndicator: values.coverDraftIndicator,
      })
    }
  }

  useEffect(() => {
    /* istanbul ignore else */
    if (guid) {
      getAccountDetails()
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id, guid])

  return (
    <AccountDetailsContext.Provider
      value={{
        accountDetails,
        isAccountDetailsLoading,
        hasAccountDetailsError,
        updateAccountNickname:
          mockState?.updateAccountNickname || updateAccountNickname,
        updateCoverDraft: mockState?.updateCoverDraft || coverDraftUpdate,
        updateRaiseYourRate:
          mockState?.updateRaiseYourRate || updateRaiseYourRate,
      }}
    >
      {children}
    </AccountDetailsContext.Provider>
  )
}

export const useAccountDetailsContext = (): AccountDetailsProviderProps => {
  const context = useContext(AccountDetailsContext)
  /* istanbul ignore next */
  if (context === undefined) {
    throw new Error(
      'useAccountDetailsContext must be used within AccountDetailsProvider',
    )
  }
  return context
}
